'use client';

import { useEffect, useRef, useState } from 'react';
import { Button, ComboBox, Key } from 'react-aria-components';

import ChevronIcon from '@/fo/icons/base/icons/chevron24.svg';
import { UiKitInputSearch } from '@/fo/ui/kit/input/search';
import { Option, UiKitListboxBase } from '@/fo/ui/kit/listbox/base';

import { styles } from './UiKitSelectBase.styles';

export interface UiKitSelectBaseProps<T extends Option> {
  options: T[];
  disabled?: boolean;
  label?: string;
  placeholder?: string;
  helperText?: string;
  onChange?: (option: T | null) => void;
  errorMessage?: string;
  selected?: T | null;
  defaultSelected?: T | null;
  className?: string;
  withBorder?: boolean;
  noInput?: boolean;
  inputClassName?: string;
  name?: string;
}

export function UiKitSelectBase<T extends Option>({
  options,
  disabled,
  onChange,
  helperText,
  errorMessage,
  placeholder,
  selected,
  defaultSelected,
  label,
  withBorder = true,
  className,
  noInput = false,
  inputClassName,
  name
}: UiKitSelectBaseProps<T>) {
  const slots = styles({ noInput });
  const triggerRef = useRef<HTMLDivElement>(null);

  const handleChange = (key: Key | null) => {
    const option = options.find((option) => option.value === key);
    onChange?.(option ?? null);
    setInputValue(option?.label ?? '');
  };

  const [inputValue, setInputValue] = useState('');

  useEffect(() => {
    if (!inputValue) {
      setInputValue(selected?.label ?? '');
    }
  }, [inputValue, selected?.label]);

  return (
    <div className={slots.base({ className })}>
      <ComboBox
        selectedKey={selected?.value}
        defaultSelectedKey={defaultSelected?.value}
        isDisabled={disabled}
        onSelectionChange={handleChange}
        isInvalid={!!errorMessage}
        menuTrigger="focus"
        inputValue={inputValue}
        name={name}
      >
        <UiKitInputSearch
          ref={triggerRef}
          className={slots.input({ className: inputClassName })}
          helperText={helperText}
          errorMessage={errorMessage}
          placeholder={placeholder}
          label={label}
          withBorder={withBorder}
          endAdornment={
            <Button className={slots.button()}>
              <ChevronIcon className={slots.icon()} />
            </Button>
          }
          readOnly={noInput}
        />
        <UiKitListboxBase
          options={options}
          triggerRef={triggerRef}
        />
      </ComboBox>
    </div>
  );
}
